import { Divider } from '@material-ui/core';
import React, { useState } from 'react';
import {
  Show,
  ShowProps,
  useShowController,
  SimpleShowLayout,
  FunctionField,
  TextField,
  FileField,
  useGetIdentity,
  useTranslate,
  useMutation,
  useNotify,
  useRedirect,
} from 'react-admin';

import CustomSelect from '../../../components/CustomSelect';
import DoctorReportForm from '../../../components/DoctorReportForm';
import PharmacyReportForm from '../../../components/PharmacyReportForm';
import SectionTitle from '../../../components/SectionTitle';
import TestHistory from '../../../components/TestHistory';
import TestResults from '../../../components/TestResults';
import { TestNames } from '../../../constants/test/testNames';
import { PartnerNames } from '../../../constants/partner/partnerNames';
import { TestStatus } from '../../../interfaces/tests.interfaces';
import DoctorsReport from '../../../components/DoctorsReport';
import IdunoxReportLink from '../../../components/IdunoxReportLink';
import messages from '../../../components/DoctorReportForm/messages';

import './styles.scss';

const calculateAge = (dateOfBirth: string) => {
  const date = new Date(dateOfBirth);
  const diff = Date.now() - date.getTime();
  const ageDate = new Date(diff);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

const LANGUAGE_MAP: { [key: string]: string } = {
  en: 'English',
  de: 'German',
  fr: 'French',
  it: 'Italian',
};

const getMedicationName = (user: any) => {
  const { takingMedicationRegularly, medicationName } = user;
  let medication = '-';

  if (takingMedicationRegularly === 'yes') {
    medication = medicationName || 'Name not provided';
  }

  if (takingMedicationRegularly === 'no') {
    medication = 'None';
  }

  return medication;
};

const getSyphilisTreatmentYear = (user: any) => {
  const { stdTreated, whenWasSyphilisTreated } = user;
  let value = '-';

  if (stdTreated && stdTreated.includes('treatedForSyphilis')) {
    value = whenWasSyphilisTreated;

    if (whenWasSyphilisTreated === 'longerAgo') {
      value = 'Longer ago';
    }
  }

  if (stdTreated && stdTreated.includes('none')) {
    value = 'No';
  }

  return value;
};

const getHepatitisBVaccinated = (user: any) => {
  const { stdTreated } = user;
  let value = '-';

  if (stdTreated && stdTreated.includes('hepatitisBVaccinated')) {
    value = 'Yes';
  }

  return value;
};

const getBloodPressureValue = (user: any) => {
  const { bloodPressureMesuredValue } = user;
  let value = '-';

  if (bloodPressureMesuredValue) {
    value = `${bloodPressureMesuredValue} mmHg`;
  }

  return value;
};

const getDoctrosReportData = (record: any) => {
  const { results } = record;

  if (results && results.services) {
    const doctorsReportData = results.services.find(
      (service: any) => service.service === 'doctorsReport',
    );

    if (doctorsReportData) {
      const { doctorsName, doctorsReport } = doctorsReportData;
      return { doctorsName, doctorsReport };
    }
    return null;
  }

  return null;
};

const getDoctorRecommendedActionsValue = (
  recommendedActions: string[] | undefined | null,
  action: string,
) => {
  if (!recommendedActions) {
    return '';
  }

  return recommendedActions.includes(action) ? 'true' : 'false';
};

const ReviewedTestsShow = (props: ShowProps) => {
  const notify = useNotify();
  const translate = useTranslate();
  const redirect = useRedirect();
  const { identity } = useGetIdentity();
  const { record, error, loading } = useShowController(props);
  const isS24 = identity?.partnerCode === 's24';
  const isStauffacher = identity?.partnerCode === 'stauffacher';
  const isAdmin = identity?.userRoles.includes('admin');
  const isLongevityCheck = record?.type === 'longevityCheck';

  const [doctorName, setDoctorName] = useState<string>('Dr. R. Döring');
  const [report, setReport] = useState<string>('');

  const getDataForUpdate = () => ({
    report,
    doctorName,
    isPharmacyReport: false,
    pharmacyName: null,
  });

  const [addReport, { loading: addReportLoading }] = useMutation(
    {
      type: 'update',
      resource: 'tests',
      payload: {
        id: record?.id,
        data: getDataForUpdate(),
      },
    },
    {
      onSuccess: () => {
        redirect('/tests/doctorReportNeededStateTests');
        notify(messages.addReportSuccessMessage, { type: 'success' });
      },
      onFailure: () =>
        notify(messages.addReportErrorMessage, { type: 'warning' }),
    },
  );

  if (loading) {
    return <div>Loading</div>;
  }

  if (error) {
    return <div>Error {error}</div>;
  }

  const doctorsReportData = record && getDoctrosReportData(record);
  const adjustDietWithNutritionAdvice = getDoctorRecommendedActionsValue(
    record?.doctorRecommendedActions,
    'adjustDietWithNutritionAdvice',
  );

  return (
    <Show {...props} className="testShow">
      <SimpleShowLayout>
        <SectionTitle title={translate('testResults.userData')} />
        <FunctionField
          className="userDataText text"
          render={() => translate(`gender.${record?.user.biologicalGender}`)}
          label="testResults.gender"
        />
        <TextField
          className="userDataText text"
          source="user.formattedDateOfBirth"
          label="testResults.dateOfBirth"
          emptyText="-"
        />
        <FunctionField
          className="userDataText text"
          render={() => calculateAge(record?.user.dateOfBirth)}
          label="testResults.age"
        />
        <FunctionField
          className="userDataText text"
          render={() => LANGUAGE_MAP[record?.user.lang] || '-'}
          label="testResults.userLanguage"
        />
        {/* TODO: Resolve this better */}
        {!isS24 && !isStauffacher && !isLongevityCheck && (
          <TextField
            className="userDataText text"
            source="user.diet"
            label="testResults.diet"
            emptyText="-"
          />
        )}
        {!isS24 && !isStauffacher && !isLongevityCheck && (
          <FunctionField
            className="userDataText text"
            render={() => getMedicationName(record?.user)}
            label="Meds in use"
          />
        )}
        {/* TODO: Resolve this better */}
        {(isS24 || isStauffacher) && (
          <TextField
            className="userDataText text"
            source="user.phoneNumber"
            label="testResults.phoneNumber"
            emptyText="+381658810686"
          />
        )}
        {(isAdmin || isStauffacher) && (
          <TextField
            className="userDataText text flexyDataText"
            source="user.email"
            label="testResults.email"
            emptyText="email@example.com"
          />
        )}
        <br />
        {isAdmin && !isLongevityCheck && (
          <FunctionField
            className="userDataText text"
            render={() => getSyphilisTreatmentYear(record?.user)}
            label="testResults.healedSyphilis"
          />
        )}
        {isAdmin && !isLongevityCheck && (
          <FunctionField
            className="userDataText text"
            render={() => getHepatitisBVaccinated(record?.user)}
            label="testResults.hepatitisBVaccinated"
            emptyText="-"
          />
        )}
        {isAdmin && !isLongevityCheck && (
          <TextField
            className="userDataText text"
            source="user.smoking"
            label="testResults.smoking"
            emptyText="-"
          />
        )}
        {isAdmin && !isLongevityCheck && (
          <TextField
            className="userDataText text flexyDataText"
            source="user.heartAttackInFamily"
            label="testResults.heartAttackInFamily"
            emptyText="-"
          />
        )}
        {isAdmin && !isLongevityCheck && (
          <FunctionField
            className="userDataText text flexyDataText"
            render={() => getBloodPressureValue(record?.user)}
            label="testResults.bloodPressureMesuredValue"
          />
        )}
        <Divider className="divider" />
        <TestHistory record={record} />
        <SectionTitle title={translate('testResults.testData')} />
        {isAdmin && record?.partnerCode && (
          <FunctionField
            className="text"
            render={() =>
              PartnerNames[record?.partnerCode as keyof typeof PartnerNames]
            }
            label="testResults.partner"
            emptyText="-"
          />
        )}
        <FileField
          target="_blank"
          className="text--purple"
          source="results.labReportSignedUrl"
          title={translate('testResults.openLabReport')}
          label="testResults.labReport"
        />
        {isLongevityCheck && record?.idunoxData?.results?.reportURL && (
          <IdunoxReportLink
            addLabel
            className="text--purple"
            testId={record.id}
            label="testResults.idunoxReport"
          />
        )}
        <FunctionField
          className="text"
          render={() => TestNames[record?.type as keyof typeof TestNames]}
          label="testResults.testType"
        />
        {record && (
          <TestResults
            data={record.results.tests}
            partnerCode={record.partnerCode}
            userInfo={record.user}
            readonly
          />
        )}
        <Divider className="divider" />
        {isLongevityCheck && (
          <>
            <SectionTitle title={translate('testResults.diagnosis')} />
            <div className="longevityCheckConfigurator">
              <div className="longevityCheckConfigurator__item--title">
                {translate(
                  'healthRiskCheck.configurator.configureActionsToBeShown',
                )}
              </div>
              <div className="longevityCheckConfigurator__item">
                <div>
                  {translate(
                    'healthRiskCheck.configurator.adjustDietWithNutritionAdvice',
                  )}
                </div>
                <CustomSelect
                  emptyLabel={translate(
                    'healthRiskCheck.configurator.selectYesOrNo',
                  )}
                  options={[
                    {
                      value: 'true',
                      label: translate('healthRiskCheck.configurator.yes'),
                    },
                    {
                      value: 'false',
                      label: translate('healthRiskCheck.configurator.no'),
                    },
                  ]}
                  value={adjustDietWithNutritionAdvice}
                  disabled
                />
              </div>
            </div>
            {doctorsReportData && doctorsReportData.doctorsReport !== '' && (
              <>
                <Divider className="divider" />
                <SectionTitle title={translate('testResults.doctorsReport')} />
                <DoctorsReport
                  doctorsName={doctorsReportData.doctorsName}
                  doctorsReport={doctorsReportData.doctorsReport}
                />
              </>
            )}
            <Divider className="divider" />
          </>
        )}
        {record?.results?.resultState === TestStatus.DOCTOR_REPORT_PENDING && (
          <>
            <SectionTitle title={translate('testResults.reportForm')} />
            <DoctorReportForm
              addReport={addReport}
              addReportLoading={addReportLoading}
              doctorName={doctorName}
              setDoctorName={setDoctorName}
              report={report}
              setReport={setReport}
            />
          </>
        )}

        {record?.results?.resultState ===
          TestStatus.PHARMACY_REPORT_PENDING && (
          <>
            {doctorsReportData && doctorsReportData.doctorsReport !== '' && (
              <>
                <SectionTitle title={translate('testResults.doctorsReport')} />
                <DoctorsReport
                  doctorsName={doctorsReportData.doctorsName}
                  doctorsReport={doctorsReportData.doctorsReport}
                />
                <Divider className="divider" />
              </>
            )}
            <SectionTitle title={translate('testResults.pharmacyReport')} />
            <PharmacyReportForm record={record} />
          </>
        )}
      </SimpleShowLayout>
    </Show>
  );
};

export default ReviewedTestsShow;
